import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Group, Rect, Text } from 'react-konva';
import DrawingText from './DrawingText';
import { getShapeBounds } from '../utils/drawingUtils';
import { hexToRgba } from '../utils/colorUtils';

const LockedScaleOverlay = ({ allShapes = [], allScales = [], stageScale = 1, onSelectShapes, selectedShapeIds = [] }) => {
  const { t } = useTranslation();
  const [hoveredGroup, setHoveredGroup] = useState(null);

  const groups = useMemo(() => {
    const lockedShapes = allShapes.filter(s => s.scaleKey && s.visible !== false);
    if (!lockedShapes.length) return [];

    const byScale = {};
    lockedShapes.forEach(shape => {
      const key = shape.scaleKey;
      if (!byScale[key]) byScale[key] = [];
      byScale[key].push(shape);
    });

    return Object.entries(byScale).map(([scaleKey, shapes]) => {
      const scale = allScales.find(s => s.key === scaleKey);
      const label = scale ? scale.label : scaleKey;

      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      shapes.forEach(shape => {
        const bounds = getShapeBounds(shape);
        if (bounds) {
          minX = Math.min(minX, bounds.x);
          minY = Math.min(minY, bounds.y);
          maxX = Math.max(maxX, bounds.x + bounds.width);
          maxY = Math.max(maxY, bounds.y + bounds.height);
        }
      });

      if (!isFinite(minX)) return null;

      const padding = 8 / stageScale;
      const headerSpace = Math.max(16, 20 / stageScale);
      const rx = minX - padding;
      const ry = minY - padding;
      const rw = maxX - minX + padding * 2;
      const rh = maxY - minY + padding * 2 + headerSpace;

      return { label, count: shapes.length, shapeIds: shapes.map(s => s.id), x: rx, y: ry, width: rw, height: rh, headerSpace };
    }).filter(Boolean);
  }, [allShapes, allScales, stageScale]);

  if (!groups.length) return null;

  const btnSize = 16 / stageScale;
  const headerPadding = 4 / stageScale;

  return (
    <Group>
      {groups.map((g, i) => {
        const isHovered = hoveredGroup === i;
        const btnX = g.x + g.width - btnSize - headerPadding;

        const hasSelection = selectedShapeIds.length > 0;
        const groupHasSelected = selectedShapeIds.some(id => g.shapeIds.includes(id));
        const groupOpacity = hasSelection && !groupHasSelected ? 0.25 : 1;

        return (
          <React.Fragment key={i}>
            <Rect
              x={g.x}
              y={g.y}
              width={g.width}
              height={g.height}
              stroke="#000000ff"
              strokeWidth={1.5 / stageScale}
              dash={[20, 5]}
              fill="rgba(0, 0, 0, 0.05)"
              cornerRadius={4 / stageScale}
              listening={false}
              opacity={groupOpacity}
            />
            <Rect
              x={g.x}
              y={g.y + g.height - g.headerSpace}
              width={g.width}
              height={g.headerSpace}
              fill={hexToRgba('#9191afff', 0.55)}
              cornerRadius={[0, 0, 4 / stageScale, 4 / stageScale]}
              listening={false}
              opacity={groupOpacity}
            />
            <DrawingText
              x={g.x + 10 / stageScale}
              y={g.y + g.height - g.headerSpace + (g.headerSpace - 14 / stageScale) / 2}
              text={`${t('replan.contextualToolPanel.locked_scale_label')}: ${g.label} (${g.count})`}
              fontSize={12 / stageScale}
              fontStyle="bold"
              fill="#000000ff"
              useBadge={false}
              stageScale={stageScale}
              align="left"
              listening={false}
              opacity={groupOpacity}
            />
            {onSelectShapes && (
              <Group
                x={btnX}
                y={g.y + g.height - g.headerSpace + (g.headerSpace - btnSize) / 2}
                onClick={() => onSelectShapes(g.shapeIds)}
                onTap={() => onSelectShapes(g.shapeIds)}
                onMouseEnter={() => setHoveredGroup(i)}
                onMouseLeave={() => setHoveredGroup(null)}
              >
                <Rect
                  width={btnSize}
                  height={btnSize}
                  fill={isHovered ? hexToRgba('#000000', 0.15) : 'transparent'}
                  cornerRadius={3 / stageScale}
                  listening={true}
                />
                <Text
                  x={btnSize / 2}
                  y={btnSize / 2}
                  text="👁"
                  fontSize={10 / stageScale}
                  fill="#000000ff"
                  offsetX={5 / stageScale}
                  offsetY={5 / stageScale}
                  listening={false}
                />
              </Group>
            )}
          </React.Fragment>
        );
      })}
    </Group>
  );
};

export default LockedScaleOverlay;
